import React, { useState, useEffect, useRef } from "react";
import ImagePicker from "./ImagePicker.jsx";

const SLUG_TO_TYPE = {
  "meet": "meet", "communicate": "communicate", "sensory": "sensory",
  "passions": "passions", "day": "day", "working-on": "working_on",
  "hard-times": "hard_times", "health": "health", "people": "people",
  "community": "community",
};

/**
 * Images panel shown in the coordinator section editor.
 * Manages section_strip placements for one section.
 */
export default function SectionImagePanel({ clientId, sectionSlug }) {
  const sectionType = SLUG_TO_TYPE[sectionSlug] || sectionSlug;

  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const existingIds = placements.map(p => p.media_id);

  async function fetchPlacements() {
    try {
      const res = await fetch(
        `/api/clients/${clientId}/placements?type=section_strip&target=${sectionType}`
      );
      const data = await res.json();
      setPlacements(Array.isArray(data) ? data.sort((a, b) => a.order - b.order) : []);
    } catch { /* silent */ }
    setLoading(false);
  }

  useEffect(() => { fetchPlacements(); }, [clientId, sectionType]);

  async function addFromLibrary(item) {
    // ImagePicker.onSelect passes the full media object; older callers passed a
    // bare id. Accept either.
    const mediaId = (item && typeof item === "object") ? item.id : item;
    if (!mediaId) return;
    await fetch(`/api/clients/${clientId}/placements`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        media_id: mediaId,
        placement_type: "section_strip",
        placement_target: sectionType,
      }),
    });
    fetchPlacements();
  }

  async function uploadAndAdd(file) {
    setUploading(true);
    try {
      // 1. Upload
      const form = new FormData();
      form.append("file", file);
      const uploadRes = await fetch(`/api/clients/${clientId}/media`, {
        method: "POST",
        body: form,
      });
      if (!uploadRes.ok) {
        const body = await uploadRes.json().catch(() => ({}));
        alert(body.error || "Upload failed");
        return;
      }
      const media = await uploadRes.json();

      // 2. Place
      await fetch(`/api/clients/${clientId}/placements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          media_id: media.id,
          placement_type: "section_strip",
          placement_target: sectionType,
        }),
      });
      fetchPlacements();
    } finally {
      setUploading(false);
    }
  }

  async function removeFromStrip(placementId) {
    await fetch(`/api/placements/${placementId}`, { method: "DELETE" });
    fetchPlacements();
  }

  if (loading) return null;

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.heading}>Images for this section's scene</h3>
          {/* Where these go changed on Sep 28: the DSP pages no longer show a
              strip of stills. The same pictures appear in this part of the
              introduction, narrated and moving. Say so, or the coordinator
              looks for a strip that isn't there. */}
          <p style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: "2px 0 0" }}>
            These become the pictures in this part of the introduction. Support staff see them in the stream, not as a strip on the page.
          </p>
        </div>
        <div style={styles.actions}>
          <button style={styles.addBtn} onClick={() => setPickerOpen(true)}>
            + Add from library
          </button>
          <button
            style={styles.uploadBtn}
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
          >
            {uploading ? "Uploading…" : "↑ Upload new"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={e => e.target.files[0] && uploadAndAdd(e.target.files[0])}
          />
        </div>
      </div>

      {placements.length === 0 ? (
        <p style={styles.empty}>
          No images yet. Add a few and this part of the introduction has pictures to show.
        </p>
      ) : (
        <div style={styles.strip}>
          {placements.map(p => (
            <div key={p.id} style={styles.thumb}>
              <img src={p.thumb_url} alt={p.alt_text || ""} style={styles.thumbImg} />
              <button
                style={styles.removeBtn}
                onClick={() => removeFromStrip(p.id)}
                title="Remove from this section"
                aria-label="Remove image"
              >✕</button>
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <ImagePicker
          clientId={clientId}
          onSelect={addFromLibrary}
          onClose={() => setPickerOpen(false)}
          multi
          excludeIds={existingIds}
        />
      )}
    </div>
  );
}

const styles = {
  panel: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)", flexWrap: "wrap" },
  heading: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-secondary)" },
  actions: { display: "flex", gap: "var(--space-2)" },
  addBtn: { fontSize: "var(--text-xs)", color: "var(--color-accent)", background: "var(--color-accent-subtle)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-1) var(--space-3)", cursor: "pointer" },
  uploadBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-1) var(--space-3)", cursor: "pointer" },
  empty: { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", fontStyle: "italic" },
  strip: { display: "flex", gap: "var(--space-3)", flexWrap: "wrap" },
  thumb: { position: "relative", width: 160, height: 160, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  thumbImg: { display: "block", maxWidth: "100%", maxHeight: 160, width: "auto", height: "auto", borderRadius: 8 },
  removeBtn: { position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: "var(--radius-full)", width: 18, height: 18, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", lineHeight: 1 },
};
